import { encodePath, joinURL } from 'ufo';

import { createOperationsGenerator, defineProvider } from '@nuxt/image/runtime';

/**
 * Modified from the original:
 * https://raw.githubusercontent.com/nuxt/image/9be356e3f3cefe91d00641b303da5f3dc447524f/src/runtime/providers/sanity.ts
 */

const operationsGenerator = createOperationsGenerator({
  keyMap: {
    format: 'fm',
    height: 'h',
    quality: 'q',
    width: 'w',
    // Convenience modifiers
    background: 'bg',
    download: 'dl',
    dpr: 'dpr',
    sharpen: 'sharp',
    orientation: 'or',
    'min-height': 'min-h',
    'max-height': 'max-h',
    'min-width': 'min-w',
    'max-width': 'max-w',
    minHeight: 'min-h',
    maxHeight: 'max-h',
    minWidth: 'min-w',
    maxWidth: 'max-w',
    saturation: 'sat',
  },
  valueMap: {
    format: {
      jpeg: 'jpg',
    },
    fit: {
      cover: 'crop',
      contain: 'fill',
      fill: 'scale',
      inside: 'min',
      outside: 'max',
    },
  },
  formatter: (key, value: string | true | number | Record<string, unknown>) =>
    String(value) === 'true' ? key : encodePath(`${key}=${value}`),
});

const getMetadata = (id: string) => {
  const result = id.match(/-(?<width>\d*)x(?<height>\d*)-(?<format>.*)$/);
  if (!result || !result.groups) {
    // Invalid Sanity image asset ID
    if (import.meta.dev) {
      console.warn(`An invalid image asset ID was passed in: ${id}`);
    }
    return { width: undefined, height: undefined, format: undefined };
  }

  const width = Number(result.groups.width);
  const height = Number(result.groups.height);

  return {
    width,
    height,
    format: result.groups.format,
  };
};

export default defineProvider({
  getImage: (src, { modifiers = {} }) => {
    const mutableModifiers: Partial<
      Record<string, string | number | true | Record<string, unknown>>
    > = { ...modifiers };

    // default to max
    if (!mutableModifiers.fit) {
      mutableModifiers.fit = 'max';
    }

    const { height: sourceHeight, width: sourceWidth } = getMetadata(src);
    if (
      mutableModifiers.crop &&
      typeof mutableModifiers.crop !== 'string' &&
      sourceWidth &&
      sourceHeight
    ) {
      const left = (mutableModifiers.crop as { left: number }).left * sourceWidth;
      const top = (mutableModifiers.crop as { top: number }).top * sourceHeight;
      const right = sourceWidth - (mutableModifiers.crop as { right: number }).right * sourceWidth;
      const bottom =
        sourceHeight - (mutableModifiers.crop as { bottom: number }).bottom * sourceHeight;
      mutableModifiers.rect = [left, top, right - left, bottom - top]
        .map((i) => i.toFixed(0))
        .join(',') as `${number},${number},${number},${number}`;
      delete mutableModifiers.crop;
    }
    if (mutableModifiers.hotspot && typeof mutableModifiers.hotspot !== 'string') {
      mutableModifiers['fp-x'] = (mutableModifiers.hotspot as { x: number }).x;
      mutableModifiers['fp-y'] = (mutableModifiers.hotspot as { y: number }).y;
      delete mutableModifiers.hotspot;
    }
    if (!mutableModifiers.format || mutableModifiers.format === 'auto') {
      if (mutableModifiers.format === 'auto') {
        delete mutableModifiers.format;
      }
      mutableModifiers.auto = 'format';
    }
    if (mutableModifiers.fit === 'contain' && !mutableModifiers.bg) {
      mutableModifiers.bg = 'ffffff';
    }
    const operations = operationsGenerator(mutableModifiers);

    const parts = src.split('-').slice(1);
    const format = parts.pop();
    const isSvg = format === 'svg';

    let operationsBits = operations ? operations.split('/').filter(Boolean) : [];

    // always choose the smallest `w` value
    const globalMaxImageWidth = 1600;
    const foundW = operationsBits
      .filter((bit) => bit.startsWith('w='))
      .map((bit) => parseInt(bit.split('=')[1], 10))
      .map((w) => (w > globalMaxImageWidth ? globalMaxImageWidth : w))
      .sort((lhs, rhs) => {
        return lhs - rhs;
      });

    if (foundW.length > 0) {
      operationsBits = operationsBits.filter((bit) => !bit.startsWith('w='));
      const w = foundW[0];
      operationsBits.unshift(`w=${w}`);
    }

    // "default" to fit=max
    if (
      operationsBits.length > 0 &&
      operationsBits.find((bit) => bit.startsWith('fit=')) === undefined
    ) {
      operationsBits.unshift('fit=max');
    }
    // "default" to fm=webp
    if (
      operationsBits.length > 0 &&
      operationsBits.find((bit) => bit.startsWith('fm=')) === undefined
    ) {
      operationsBits.unshift('fm=webp');
    }
    if (isSvg) {
      operationsBits = [];
    }

    // not sure why it was needed to do the `split('/').join('&')` here
    const filenameAndQueries =
      parts.join('-') +
      '.' +
      format +
      (operationsBits.length > 0 ? '?' + operationsBits.join('&') : '');

    return {
      url: joinURL('/', 'cdn-images', filenameAndQueries),
    };
  },
});
